import { Button, Card, Checkbox, message, Radio, Space } from "antd";
import { useEffect, useState } from "react";
import "./ManualReview.css";
import { useNavigate, useParams } from "react-router";
import { AssesmentUrl } from "../../../../Constants/Constants";
import axios from "axios";

const parseQuestion = (raw) => {
  if (!raw) return { question_text: "", image_url: [] };

  try {
    let parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

    // Handle double-stringified case
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    const image_url = Array.isArray(parsed?.image_url)
      ? parsed.image_url
      : typeof parsed?.image_url === "string" && parsed.image_url
      ? [parsed.image_url]
      : [];

    return {
      question_text: parsed?.question_text || "",
      image_url,
    };
  } catch (error) {
    console.error("Question parse error:", error, "raw:", raw);
    return { question_text: "", image_url: [] };
  }
};

const getQuestionType = (questionId = "") => {
  if (questionId.startsWith("mcqid")) return "mcq";
  if (questionId.startsWith("tfid")) return "tf";
  if (questionId.startsWith("fbid")) return "fb";
  if (questionId.startsWith("fbdid")) return "fbd";
  if (questionId.startsWith("maid")) return "ma";
  if (questionId.startsWith("mlid")) return "ml";
  if (questionId.startsWith("table_qid")) return "table_q";
  if (questionId.startsWith("imgid")) return "img";
  return "mcq";
};

const toArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string" && val.includes(","))
    return val.split(",").map((s) => s.trim());
  return val ? [val] : [];
};

const formatQuestion = (text = "") => {
  let q = text.replace(/\\n\s*([a-zA-Z])\./g, "<br/>$1.");
  q = q.replace(/(?:^|\s)([a-z])\./g, (match, p1) =>
    match.replace(p1, p1.toUpperCase()),
  );
  q = q.replace(/\\n(?!\s*[a-zA-Z]\.)/g, "<br/>");
  q = q.replace(/\s*<br\/>\s*/g, "<br/>");
  return q;
};

// ─── Sub-renderers ────────

const RadioReview = ({ options, userAnswer, correctAnswer, isCorrect }) => {
  const correctArr = toArray(correctAnswer);
  const validOptions = options.filter(
    (o) => o && o.trim() !== "" && o !== "None",
  );

  return (
    <Radio.Group value={Array.isArray(userAnswer) ? null : userAnswer}>
      <Space direction="vertical">
        {validOptions.map((opt) => {
          const isCorrectOpt = correctArr.includes(opt);
          const isUserOpt = Array.isArray(userAnswer)
            ? userAnswer.includes(opt)
            : opt === userAnswer;

          let className = "";
          if (isCorrectOpt) className = "radio-green";
          else if (!isCorrect && isUserOpt) className = "radio-incorrect";

          return (
            <Radio key={opt} value={opt} className={className}>
              {opt}
            </Radio>
          );
        })}
      </Space>
    </Radio.Group>
  );
};

const CheckboxReview = ({ options, userAnswer, correctAnswer, isCorrect }) => {
  const correctArr = toArray(correctAnswer);
  const userArr = toArray(userAnswer);
  const validOptions = options.filter((o) => o && o.trim() !== "");

  return (
    <Checkbox.Group value={userArr}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {validOptions.map((opt) => {
          const isCorrectOpt = correctArr.includes(opt);
          const isUserOpt = userArr.includes(opt);

          let className = "";
          if (isCorrectOpt) className = "radio-green";
          else if (!isCorrect && isUserOpt) className = "radio-incorrect";

          return (
            <Checkbox key={opt} value={opt} className={className}>
              {opt}
            </Checkbox>
          );
        })}
      </Space>
    </Checkbox.Group>
  );
};

const TextReview = ({ userAnswer, correctAnswer, isCorrect }) => (
  <div className="textAnswers">
    <p>
      <strong>Your Answer:</strong>{" "}
      <span style={{ color: isCorrect ? "#389e0d" : "#cf1322" }}>
        {userAnswer || <em>No answer provided</em>}
      </span>
    </p>
    {!isCorrect && (
      <p>
        <strong>Correct Answer:</strong>{" "}
        <span style={{ color: "#389e0d" }}>{correctAnswer}</span>
      </p>
    )}
  </div>
);

const MatchingReview = ({ options, userAnswer, correctAnswer, isCorrect }) => {
  const correctArr = toArray(correctAnswer);
  const userArr = toArray(userAnswer);
  const validOptions = options.filter((o) => o && o.trim() !== "");

  return (
    <div style={{ width: "100%" }}>
      {validOptions.map((opt, i) => {
        const [term = ""] = opt.split(" - ");
        const correctDesc =
          (correctArr[i] || "").split(" - ")[1] || correctArr[i] || "";
        const isMatch =
          (userArr[i] || "").trim() === (correctArr[i] || "").trim();

        return (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: "8px",
              padding: "9px 14px",
              marginBottom: "6px",
              borderRadius: "8px",
              background: isMatch ? "#eafff0" : "#ffeaea",
              border: `1px solid ${isMatch ? "#a2ffbb" : "#ffa2a2"}`,
            }}
          >
            <span style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 13 }}>
              {term}
            </span>
            <span
              style={{ color: isMatch ? "#237804" : "#cf1322", fontSize: 13 }}
            >
              {correctDesc}
            </span>
          </div>
        );
      })}
      {!isCorrect && (
        <div className="textAnswers" style={{ marginTop: 10 }}>
          <p>
            <strong>Your Answer:</strong>{" "}
            <span style={{ color: "#cf1322" }}>
              {Array.isArray(userAnswer) ? userAnswer.join(", ") : userAnswer}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ─────

const ManualReview = () => {
  const navigate = useNavigate();

  const { id, assessmentId } = useParams();

  const profileDetails = JSON.parse(localStorage.getItem("Profile"));
  const userId = profileDetails.user_id;

  const [reviewData, setReviewData] = useState([]);
  const [seconds, setSeconds] = useState(20);

  // ── Auto redirect after 20 seconds ──
  useEffect(() => {
    if (seconds === 0) {
      navigate("/");
      return;
    }

    const timerInterval = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [seconds]);

  // ── Fetch review answers ──
  useEffect(() => {
    let reviewAnswersUrl = `${AssesmentUrl}manual_course_review_answers`;

    let body = {
      course_id: id,
      assessment_id: assessmentId,
      user_id: userId,
    };

    axios
      .post(reviewAnswersUrl, body)
      .then((res) => {
        console.log(res, "reviewAnswersRes");

        let response = res.data;

        if (response.status === 200) {
          setReviewData(response.review_answers_details);
        } else {
          setReviewData([]);
        }
      })
      .catch((err) => {
        message.error(err.message);
      });
  }, []);

  // ── Render answer section by question type ──
  const renderAnswerSection = (item) => {
    const qType = getQuestionType(item.questionId);
    const { options = [], userAnswer, correctAnswer, isCorrect } = item;

    switch (qType) {
      case "mcq":
      case "tf":
      case "img":
      case "table_q":
        return (
          <RadioReview
            options={options}
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        );

      case "fb": {
        const hasOptions = options.some((o) => o && o.trim() !== "");
        return hasOptions ? (
          <RadioReview
            options={options}
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        ) : (
          <TextReview
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        );
      }

      case "fbd": {
        const hasOptions = options.some((o) => o && o.trim() !== "");
        return hasOptions ? (
          <RadioReview
            options={options}
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        ) : (
          <TextReview
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        );
      }

      case "ma":
        return (
          <CheckboxReview
            options={options}
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        );

      case "ml":
        return (
          <MatchingReview
            options={options}
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        );

      default:
        return (
          <TextReview
            userAnswer={userAnswer}
            correctAnswer={correctAnswer}
            isCorrect={isCorrect}
          />
        );
    }
  };

  const handleDashboard = () => {
    navigate(`/`);
  };

  const totalQ = reviewData.length;
  const correctQ = reviewData.filter((i) => i.isCorrect).length;
  const wrongQ = totalQ - correctQ;

  return (
    <div className="reviewModuleMainDiv">
      <div className="timerBox">
        Redirecting to dashboard in <strong>{seconds}</strong> seconds...
      </div>

      <h2 className="reviewAnswerClass">Review Your Answers</h2>

      {/* Score summary */}
      {totalQ > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          {[
            {
              label: `Total: ${totalQ}`,
              bg: "#e6f4ff",
              color: "#0958d9",
              border: "#91caff",
            },
            {
              label: `✓ Correct: ${correctQ}`,
              bg: "#eafff0",
              color: "#389e0d",
              border: "#a2ffbb",
            },
            {
              label: `✗ Wrong: ${wrongQ}`,
              bg: "#ffeaea",
              color: "#cf1322",
              border: "#ffa2a2",
            },
          ].map(({ label, bg, color, border }) => (
            <span
              key={label}
              style={{
                padding: "6px 20px",
                borderRadius: 20,
                background: bg,
                color,
                border: `1px solid ${border}`,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Question cards */}
      {reviewData.length !== 0 &&
        reviewData.map((item, index) => {
          const qObj = parseQuestion(item.question);
          console.log("qObj for", item.questionId, qObj);

          return (
            <Card
              key={index}
              className={
                item.isCorrect ? "correctAnswerCard" : "wrongAnswerCard"
              }
            >
              {/* Correct / Incorrect badge */}
              <p
                className={
                  item.isCorrect
                    ? "correctAnswerResultType"
                    : "wrongAnswerResultType"
                }
              >
                {item.isCorrect ? "Correct Answer" : "Incorrect Answer"}
              </p>

              {/* Question number + text */}
              <p style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>
                <span
                  style={{
                    display: "inline-block",
                    marginRight: 8,
                    background: "#f0f0f0",
                    borderRadius: 6,
                    padding: "1px 9px",
                    fontSize: 13,
                    color: "#595959",
                    fontWeight: 700,
                  }}
                >
                  Q{index + 1}
                </span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatQuestion(qObj.question_text),
                  }}
                />
              </p>

              {/* Image — only for img type */}
              {Array.isArray(qObj.image_url) && qObj.image_url.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {qObj.image_url.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Q${index + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: 360,
                        objectFit: "contain",
                        borderRadius: 8,
                        display: "block",
                        margin: "0 auto 8px",
                        border: "1px solid #e8e8e8",
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Answer section — switches by question type */}
              {renderAnswerSection(item)}
            </Card>
          );
        })}

      <div className="dashboardCenter">
        <Button className="Button" type="primary" onClick={handleDashboard}>
          Go To Dashboard
        </Button>
      </div>
    </div>
  );
};

export default ManualReview;
